import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, ImageIcon, ChevronDown, Settings, GripVertical, ChevronUp } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_available: boolean;
  is_customizable: boolean;
  category_id: string | null;
  category: {
    name: string;
  } | null;
}

interface MenuCategory {
  id: string;
  name: string;
  sort_order: number;
}

interface MenuManagementProps {
  restaurantId: string;
  businessType?: string;
}

const MenuManagement = ({ restaurantId, businessType = 'restaurant' }: MenuManagementProps) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [deletingItem, setDeletingItem] = useState<MenuItem | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<MenuCategory | null>(null);
  
  const isMarketplace = businessType === 'marketplace';
  const itemLabel = isMarketplace ? 'Stock Item' : 'Menu Item';
  const itemsLabel = isMarketplace ? 'Stock Items' : 'Menu Items';
  const [customizationDialog, setCustomizationDialog] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>(null);
  const [existingCustomizations, setExistingCustomizations] = useState<any[]>([]);
  const [editingCustomization, setEditingCustomization] = useState<any | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCustomization, setNewCustomization] = useState({
    name: '',
    type: 'single_select',
    is_required: false,
    options: [{ name: '', price_type: 'free', price_amount: 0 }]
  });
  const { toast } = useToast();

  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    is_available: true,
    is_customizable: false,
    image_url: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    sort_order: ''
  });

  useEffect(() => {
    fetchData();
  }, [restaurantId]);

  const fetchData = async () => {
    try {
      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('sort_order');

      if (categoriesError) throw categoriesError;

      // Fetch menu items
      const { data: itemsData, error: itemsError } = await supabase
        .from('menu_items')
        .select(`
          *,
          category:menu_categories(name)
        `)
        .eq('restaurant_id', restaurantId)
        .order('name');

      if (itemsError) throw itemsError;

      setCategories(categoriesData || []);
      setMenuItems(itemsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load menu data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim() || !itemForm.price) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const itemData = {
        name: itemForm.name.trim(),
        description: itemForm.description.trim() || null,
        price: parseFloat(itemForm.price),
        category_id: itemForm.category_id || null,
        is_available: itemForm.is_available,
        is_customizable: itemForm.is_customizable,
        image_url: itemForm.image_url || null,
        restaurant_id: restaurantId
      };

      if (editingItem) {
        const { error } = await supabase
          .from('menu_items')
          .update(itemData)
          .eq('id', editingItem.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: `${itemLabel} updated successfully`,
        });
      } else {
        const { error } = await supabase
          .from('menu_items')
          .insert(itemData);

        if (error) throw error;

        toast({
          title: "Success",
          description: `${itemLabel} created successfully`,
        });
      }

      setIsItemDialogOpen(false);
      setEditingItem(null);
      setItemForm({ name: '', description: '', price: '', category_id: '', is_available: true, is_customizable: false, image_url: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving item:', error);
      toast({
        title: "Error",
        description: `Failed to save ${itemLabel.toLowerCase()}`,
        variant: "destructive",
      });
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast({
        title: "Error",
        description: "Category name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      const categoryData = {
        name: categoryForm.name.trim(),
        sort_order: parseInt(categoryForm.sort_order) || categories.length,
        restaurant_id: restaurantId
      };

      if (editingCategory) {
        const { error } = await supabase
          .from('menu_categories')
          .update(categoryData)
          .eq('id', editingCategory.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Category updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('menu_categories')
          .insert(categoryData);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Category created successfully",
        });
      }

      setIsCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', sort_order: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: "Error",
        description: "Failed to save category",
        variant: "destructive",
      });
    }
  };

  const confirmDeleteItem = async () => {
    if (!deletingItem) return;

    try {
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', deletingItem.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${itemLabel} deleted successfully`,
      });

      setDeletingItem(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: "Error",
        description: `Failed to delete ${itemLabel.toLowerCase()}`,
        variant: "destructive",
      });
    }
  };

  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return;

    try {
      // Check if category has items
      const itemsInCategory = menuItems.filter(item => item.category_id === deletingCategory.id);
      if (itemsInCategory.length > 0) {
        toast({
          title: "Error",
          description: `Cannot delete category with ${itemsLabel.toLowerCase()}. Please move or delete the items first.`,
          variant: "destructive",
        });
        setDeletingCategory(null);
        return;
      }

      const { error } = await supabase
        .from('menu_categories')
        .delete()
        .eq('id', deletingCategory.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category deleted successfully",
      });

      setDeletingCategory(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive",
      });
    }
  };

  const toggleItemAvailability = async (item: MenuItem) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .update({ is_available: !item.is_available })
        .eq('id', item.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${itemLabel} ${!item.is_available ? 'enabled' : 'disabled'}`,
      });

      fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
      toast({
        title: "Error",
        description: `Failed to update ${itemLabel.toLowerCase()}`,
        variant: "destructive",
      });
    }
  };

  const openItemDialog = (item?: MenuItem) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        name: item.name,
        description: item.description || '',
        price: item.price.toString(),
        category_id: item.category_id || '',
        is_available: item.is_available,
        is_customizable: item.is_customizable || false,
        image_url: item.image_url || ''
      });
    } else {
      setEditingItem(null);
      setItemForm({ name: '', description: '', price: '', category_id: '', is_available: true, is_customizable: false, image_url: '' });
    }
    setIsItemDialogOpen(true);
  };

  const openCategoryDialog = (category?: MenuCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        sort_order: category.sort_order.toString()
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', sort_order: '' });
    }
    setIsCategoryDialogOpen(true);
  };

  const fetchItemCustomizations = async (itemId: string) => {
    try {
      const { data, error } = await supabase
        .from('menu_item_customizations')
        .select(`
          *,
          customization_options (*)
        `)
        .eq('menu_item_id', itemId)
        .order('created_at');

      if (error) throw error;
      setExistingCustomizations(data || []);
    } catch (error) {
      console.error('Error fetching customizations:', error);
    }
  };

  const openCustomizationDialog = async (itemId: string, customization?: any) => {
    setSelectedMenuItem(itemId);
    await fetchItemCustomizations(itemId);
    
    if (customization) {
      setEditingCustomization(customization);
      setShowAddForm(true);
      setNewCustomization({
        name: customization.name,
        type: customization.type,
        is_required: customization.is_required,
        options: customization.customization_options?.map((opt: any) => ({
          name: opt.name,
          price_type: opt.price_type,
          price_amount: opt.price_amount
        })) || [{ name: '', price_type: 'free', price_amount: 0 }]
      });
    } else {
      setEditingCustomization(null);
      setShowAddForm(false);
      setNewCustomization({
        name: '',
        type: 'single_select',
        is_required: false,
        options: [{ name: '', price_type: 'free', price_amount: 0 }]
      });
    }
    
    setCustomizationDialog(true);
  };

  const handleSaveCustomization = async () => {
    if (!selectedMenuItem || !newCustomization.name.trim()) {
      toast({
        title: "Error",
        description: "Please fill in customization name",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingCustomization) {
        // Update existing customization
        const { error: updateError } = await supabase
          .from('menu_item_customizations')
          .update({
            name: newCustomization.name,
            type: newCustomization.type,
            is_required: newCustomization.is_required
          })
          .eq('id', editingCustomization.id);

        if (updateError) throw updateError;

        // Delete existing options
        const { error: deleteOptionsError } = await supabase
          .from('customization_options')
          .delete()
          .eq('customization_id', editingCustomization.id);

        if (deleteOptionsError) throw deleteOptionsError;

        // Insert updated options
        const optionsToInsert = newCustomization.options
          .filter(option => option.name.trim())
          .map((option, index) => ({
            customization_id: editingCustomization.id,
            name: option.name,
            price_type: option.price_type,
            price_amount: option.price_amount,
            sort_order: index
          }));

        if (optionsToInsert.length > 0) {
          const { error: optionsError } = await supabase
            .from('customization_options')
            .insert(optionsToInsert);

          if (optionsError) throw optionsError;
        }

        toast({
          title: "Success",
          description: "Customization updated successfully",
        });
      } else {
        // Insert new customization
        const { data: customizationData, error: customizationError } = await supabase
          .from('menu_item_customizations')
          .insert({
            menu_item_id: selectedMenuItem,
            name: newCustomization.name,
            type: newCustomization.type,
            is_required: newCustomization.is_required
          })
          .select()
          .single();

        if (customizationError) throw customizationError;

        // Insert options
        const optionsToInsert = newCustomization.options
          .filter(option => option.name.trim())
          .map((option, index) => ({
            customization_id: customizationData.id,
            name: option.name,
            price_type: option.price_type,
            price_amount: option.price_amount,
            sort_order: index
          }));

        if (optionsToInsert.length > 0) {
          const { error: optionsError } = await supabase
            .from('customization_options')
            .insert(optionsToInsert);

          if (optionsError) throw optionsError;
        }

        toast({
          title: "Success",
          description: "Customization added successfully",
        });
      }

      // Keep dialog open, just reset the form and refresh the list
      setEditingCustomization(null);
      setShowAddForm(false);
      setNewCustomization({
        name: '',
        type: 'single_select',
        is_required: false,
        options: [{ name: '', price_type: 'free', price_amount: 0 }]
      });
      
      // Refresh customizations list
      if (selectedMenuItem) {
        await fetchItemCustomizations(selectedMenuItem);
      }
    } catch (error) {
      console.error('Error saving customization:', error);
      toast({
        title: "Error",
        description: "Failed to save customization",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCustomization = async (customizationId: string) => {
    try {
      // Delete options first
      const { error: deleteOptionsError } = await supabase
        .from('customization_options')
        .delete()
        .eq('customization_id', customizationId);

      if (deleteOptionsError) throw deleteOptionsError;

      // Delete customization
      const { error: deleteCustomizationError } = await supabase
        .from('menu_item_customizations')
        .delete()
        .eq('id', customizationId);

      if (deleteCustomizationError) throw deleteCustomizationError;

      toast({
        title: "Success",
        description: "Customization deleted successfully",
      });

      // Refresh customizations
      if (selectedMenuItem) {
        await fetchItemCustomizations(selectedMenuItem);
      }
    } catch (error) {
      console.error('Error deleting customization:', error);
      toast({
        title: "Error",
        description: "Failed to delete customization",
        variant: "destructive",
      });
    }
  };

  const addCustomizationOption = () => {
    setNewCustomization(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price_type: 'free', price_amount: 0 }]
    }));
  };

  const removeCustomizationOption = (index: number) => {
    setNewCustomization(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateCustomizationOption = (index: number, field: string, value: any) => {
    setNewCustomization(prev => ({
      ...prev,
      options: prev.options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const moveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    const currentIndex = categories.findIndex(cat => cat.id === categoryId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;

    try {
      // Create new array with swapped positions
      const newCategories = [...categories];
      [newCategories[currentIndex], newCategories[newIndex]] = [newCategories[newIndex], newCategories[currentIndex]];

      // Update sort_order for both categories
      const updates = [
        { id: newCategories[currentIndex].id, sort_order: currentIndex },
        { id: newCategories[newIndex].id, sort_order: newIndex }
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('menu_categories')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);

        if (error) throw error;
      }

      // Update local state
      setCategories(newCategories);

      toast({
        title: "Success",
        description: "Category order updated",
      });
    } catch (error) {
      console.error('Error updating category order:', error);
      toast({
        title: "Error",
        description: "Failed to update category order",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Categories Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle>Categories</CardTitle>
            <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openCategoryDialog()} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Add Category'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="categoryName">Category Name *</Label>
                    <Input
                      id="categoryName"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="Enter category name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sortOrder">Sort Order</Label>
                    <Input
                      id="sortOrder"
                      type="number"
                      value={categoryForm.sort_order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: e.target.value })}
                      placeholder={categories.length.toString()}
                    />
                  </div>
                  <Button onClick={handleSaveCategory} className="w-full">
                    {editingCategory ? 'Update' : 'Create'} Category
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No categories yet. Create your first category to organize your menu.</p>
          ) : (
            <div className="grid gap-2">
              {categories.map((category) => (
                <div key={category.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-sm text-gray-500 ml-2">
                      ({menuItems.filter(item => item.category_id === category.id).length} items)
                    </span>
                  </div>
                   <div className="flex gap-2">
                     <Button
                       variant="ghost"
                       size="sm"
                       onClick={() => moveCategory(category.id, 'up')}
                       disabled={categories.indexOf(category) === 0}
                       title="Move up"
                     >
                       <ChevronUp className="h-4 w-4" />
                     </Button>
                     <Button
                       variant="ghost"
                       size="sm"
                       onClick={() => moveCategory(category.id, 'down')}
                       disabled={categories.indexOf(category) === categories.length - 1}
                       title="Move down"
                     >
                       <ChevronDown className="h-4 w-4" />
                     </Button>
                     <Button
                       variant="ghost"
                       size="sm"
                       onClick={() => openCategoryDialog(category)}
                     >
                       <Edit className="h-4 w-4" />
                     </Button>
                     <AlertDialog>
                       <AlertDialogTrigger asChild>
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => setDeletingCategory(category)}
                         >
                           <Trash2 className="h-4 w-4 text-red-600" />
                         </Button>
                       </AlertDialogTrigger>
                       <AlertDialogContent>
                         <AlertDialogHeader>
                           <AlertDialogTitle>Delete Category</AlertDialogTitle>
                           <AlertDialogDescription>
                             Are you sure you want to delete "{category.name}"? This action cannot be undone.
                           </AlertDialogDescription>
                         </AlertDialogHeader>
                         <AlertDialogFooter>
                           <AlertDialogCancel>Cancel</AlertDialogCancel>
                           <AlertDialogAction onClick={confirmDeleteCategory}>Delete</AlertDialogAction>
                         </AlertDialogFooter>
                       </AlertDialogContent>
                     </AlertDialog>
                   </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Menu Items Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle>{itemsLabel}</CardTitle>
            <Dialog open={isItemDialogOpen} onOpenChange={setIsItemDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openItemDialog()} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? `Edit ${itemLabel}` : `Add ${itemLabel}`}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <ImageUpload
                    bucket="menu-images"
                    currentImageUrl={itemForm.image_url}
                    onImageUploaded={(url) => setItemForm({ ...itemForm, image_url: url })}
                    onImageRemoved={() => setItemForm({ ...itemForm, image_url: '' })}
                    label={`${itemLabel} Image`}
                    maxSizeMB={5}
                  />
                  
                  <div>
                    <Label htmlFor="itemName">Item Name *</Label>
                    <Input
                      id="itemName"
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      placeholder="Enter item name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={itemForm.description}
                      onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                      placeholder="Enter item description"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">Price (D) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={itemForm.price}
                      onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={itemForm.category_id}
                      onValueChange={(value) => setItemForm({ ...itemForm, category_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="available"
                      checked={itemForm.is_available}
                      onCheckedChange={(checked) => setItemForm({ ...itemForm, is_available: checked })}
                    />
                    <Label htmlFor="available">Available for ordering</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="customizable"
                      checked={itemForm.is_customizable}
                      onCheckedChange={(checked) => setItemForm({ ...itemForm, is_customizable: checked })}
                    />
                    <Label htmlFor="customizable">Customizable item</Label>
                  </div>
                  
                  {itemForm.is_customizable && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <Label className="text-sm font-medium text-gray-700">Customization Options</Label>
                      {editingItem ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full mt-2">
                              Add Customization <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-56">
                            <DropdownMenuItem onClick={() => openCustomizationDialog(editingItem.id)}>
                              <Settings className="mr-2 h-4 w-4" />
                              Single Select
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openCustomizationDialog(editingItem.id)}>
                              <Settings className="mr-2 h-4 w-4" />
                              Multiple Select
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openCustomizationDialog(editingItem.id)}>
                              <Settings className="mr-2 h-4 w-4" />
                              Text Input
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                          Save the item first to add customization options
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button onClick={handleSaveItem} className="w-full">
                    {editingItem ? 'Update' : 'Create'} Item
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {menuItems.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No {itemsLabel.toLowerCase()} yet</p>
              <p className="text-sm text-gray-500">Add your first {itemLabel.toLowerCase()} to get started</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {menuItems.map((item) => (
                <div key={item.id} className="flex flex-col sm:flex-row items-start gap-4 p-4 border rounded-lg">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full sm:w-16 h-32 sm:h-16 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{item.name}</h3>
                      {!item.is_available && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                          Unavailable
                        </span>
                      )}
                      {item.is_customizable && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          Customizable
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="font-semibold text-green-600">D{item.price}</span>
                      {item.category && (
                        <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {item.category.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleItemAvailability(item)}
                      className="flex-1 sm:flex-none"
                    >
                      {item.is_available ? 'Disable' : 'Enable'}
                    </Button>
                    {item.is_customizable && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCustomizationDialog(item.id)}
                        className="flex-1 sm:flex-none"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openItemDialog(item)}
                      className="flex-1 sm:flex-none"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingItem(item)}
                          className="flex-1 sm:flex-none"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {itemLabel}</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{item.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={confirmDeleteItem}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customization Dialog */}
      <Dialog open={customizationDialog} onOpenChange={setCustomizationDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomization ? 'Edit Customization' : 'Manage Customizations'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Existing Customizations */}
          {existingCustomizations.length > 0 && !editingCustomization && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Existing Customizations</h3>
              <div className="space-y-2">
                {existingCustomizations.map((customization) => (
                  <div key={customization.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <div className="font-medium">{customization.name}</div>
                      <div className="text-sm text-gray-500">
                        {customization.type.replace('_', ' ')} • {customization.is_required ? 'Required' : 'Optional'}
                        {customization.customization_options?.length > 0 && (
                          <span> • {customization.customization_options.length} options</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCustomizationDialog(selectedMenuItem!, customization)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Customization</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{customization.name}"? This will also delete all its options.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteCustomization(customization.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
              <hr className="my-4" />
            </div>
          )}

          {/* Add/Edit Form */}
          {(!existingCustomizations.length || editingCustomization || showAddForm) && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="customizationName">Customization Name *</Label>
              <Input
                id="customizationName"
                value={newCustomization.name}
                onChange={(e) => setNewCustomization({ ...newCustomization, name: e.target.value })}
                placeholder="e.g., Size, Toppings, Spice Level"
              />
            </div>
            
            <div>
              <Label htmlFor="customizationType">Type</Label>
              <Select
                value={newCustomization.type}
                onValueChange={(value) => setNewCustomization({ ...newCustomization, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_select">Single Select</SelectItem>
                  <SelectItem value="multiple_select">Multiple Select</SelectItem>
                  <SelectItem value="text_input">Text Input</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="required"
                checked={newCustomization.is_required}
                onCheckedChange={(checked) => setNewCustomization({ ...newCustomization, is_required: checked })}
              />
              <Label htmlFor="required">Required customization</Label>
            </div>

            {newCustomization.type !== 'text_input' && (
              <div className="space-y-3">
                <Label>Options</Label>
                {newCustomization.options.map((option, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input
                        placeholder="Option name"
                        value={option.name}
                        onChange={(e) => updateCustomizationOption(index, 'name', e.target.value)}
                      />
                    </div>
                    <div className="w-32">
                      <Select
                        value={option.price_type}
                        onValueChange={(value) => updateCustomizationOption(index, 'price_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="fixed">Fixed Price</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {option.price_type !== 'free' && (
                      <div className="w-24">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={option.price_amount}
                          onChange={(e) => updateCustomizationOption(index, 'price_amount', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCustomizationOption(index)}
                      disabled={newCustomization.options.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addCustomizationOption}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleSaveCustomization} className="flex-1">
                {editingCustomization ? 'Update' : 'Save'} Customization
              </Button>
              {(editingCustomization || showAddForm) && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditingCustomization(null);
                    setShowAddForm(false);
                    setNewCustomization({
                      name: '',
                      type: 'single_select',
                      is_required: false,
                      options: [{ name: '', price_type: 'free', price_amount: 0 }]
                    });
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
          )}

          {/* Add New Button */}
          {existingCustomizations.length > 0 && !editingCustomization && !showAddForm && (
            <Button 
              onClick={() => {
                setShowAddForm(true);
                setEditingCustomization(null);
                setNewCustomization({
                  name: '',
                  type: 'single_select',
                  is_required: false,
                  options: [{ name: '', price_type: 'free', price_amount: 0 }]
                });
              }}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Customization
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuManagement;
